import { cloneElement, FC, ReactNode } from 'react';

import { Box, ChevronDownOutline, ChevronUpOutline, HStack, rem, Text } from '@bonusly/design-system';

import { TableHeaderTooltip } from './table-header-tooltip';

type SortableHeaderProps = {
	children: ReactNode;
	currentDirection: string;
	currentField: string;
	field: string;
	onSort: (field: string, direction: SortDirection) => void;
	textAlign?: 'left' | 'right' | 'center';
};

export enum SortDirection {
	ASC = 'ASC',
	DESC = 'DESC',
}

export const SortableHeader: FC<SortableHeaderProps> = ({ children, currentDirection, currentField, field, onSort, textAlign = 'left' }) => {
	const isSorted = currentField === field;
	const icon = currentDirection.toUpperCase() === SortDirection.ASC ? <ChevronDownOutline size="xs" /> : <ChevronUpOutline size="xs" />;
	const nextSort = isSorted ? (currentDirection.toUpperCase() === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC) : SortDirection.ASC;

	return (
		<Box as="button" onClick={() => onSort(field, nextSort)} width="100%">
			<HStack alignItems="center" justifyContent="space-between" width="100%">
				<Text
					color={isSorted ? 'text.default' : 'text.softer'}
					fontWeight={isSorted ? 'semibold' : 'normal'}
					size="sm"
					variant="medium"
					w="100%"
					textAlign={textAlign}
				>
					{children}
				</Text>
				{cloneElement(icon, {
					visibility: isSorted ? 'visible' : 'hidden',
					className: 'sort-icon',
				})}
			</HStack>
		</Box>
	);
};

type SortableHeaderWithTooltipProps = SortableHeaderProps & {
	tooltipText: string;
};

export const SortableHeaderWithTooltip: FC<SortableHeaderWithTooltipProps> = ({
	children,
	currentDirection,
	currentField,
	field,
	onSort,
	textAlign = 'left',
	tooltipText,
}) => {
	const isSorted = currentField === field;
	const icon = currentDirection.toUpperCase() === SortDirection.ASC ? <ChevronDownOutline size="xs" /> : <ChevronUpOutline size="xs" />;
	const nextSort = isSorted ? (currentDirection.toUpperCase() === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC) : SortDirection.ASC;

	// NOTE: right aligned headers have a tooltip in a div outside of the text
	if (textAlign === 'right') {
		return (
			<Box as="button" onClick={() => onSort(field, nextSort)} width="100%">
				<HStack alignItems="center" justifyContent={textAlign === 'right' ? 'flex-end' : 'space-between'} width="100%">
					<HStack spacing={0} alignItems="flex-end">
						<Text
							color={isSorted ? 'text.default' : 'text.softer'}
							fontWeight={isSorted ? 'semibold' : 'normal'}
							size="sm"
							variant="medium"
							textAlign={textAlign}
						>
							{children}
						</Text>
						<Box className="info-tooltip" height="100%" verticalAlign="bottom" mb={rem(1)}>
							<TableHeaderTooltip label={tooltipText} />
						</Box>
					</HStack>
					{cloneElement(icon, {
						visibility: isSorted ? 'visible' : 'hidden',
						className: 'sort-icon',
					})}
				</HStack>
			</Box>
		);
	}

	// NOTE: left aligned headers have a tooltip in a span inside of the text attached to the last word
	return (
		<SortableHeader currentDirection={currentDirection} currentField={currentField} field={field} onSort={onSort} textAlign={textAlign}>
			{children}
			<TableHeaderTooltip label={tooltipText} />
		</SortableHeader>
	);
};

/**
 * Use `createObjectSorter` when you want to easily sort an array of objects based on a field name and direction.
 * You configure this function by passing an object with field names as keys and functions that extract the value to sort by as values.
 * The returned values from accessor functions should be either strings or numbers.
 *
 * @example
 * const sorter = createObjectSorter({
 *   name: (user) => user.name,
 *   age: (user) => user.age,
 * });
 *
 * const sortedUsers = sorter({ field: 'name', direction: SortDirection.ASC }).sort(users);
 *
 * @param fields
 * @returns function
 */
export const createObjectSorter =
	(fields: { [key: string]: (value: any) => any }) =>
	({ field, direction }: { field: string; direction: SortDirection }) => ({
		sort: <T extends Record<string, any>>(data: T[]): T[] => {
			return data.sort((a: T, b: T) => {
				const aValue = fields[field](a);
				const bValue = fields[field](b);

				if (aValue === bValue) {
					return 0;
				}

				if (typeof aValue === 'string' && typeof bValue === 'string') {
					return direction === SortDirection.ASC ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
				}

				return direction === SortDirection.ASC ? (aValue < bValue ? -1 : 1) : aValue < bValue ? 1 : -1;
			});
		},
	});
